/**
 * The notes popup for one call, opened from the in-call bar (with Hang up while the call is on) and from any row of
 * "Recent calls": who it was, their phone and email, the booking, why they called, whether to call them back, and free
 * notes. Save sends only what changed; the server keeps who saved it and when. Closing with unsaved changes saves first; when that save fails, Close without saving leaves.
 * Once the Email field holds an address, the line under it says whether that address has a booking on the event, and one
 * button emails the caller: Resend confirmation for a booking, Send invite email for none. The server decides which, and
 * mails only the address saved on the note, so the button saves the note first.
 */
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, SelectField, TextArea, TextField } from '@/components/ui/Field';
import { INFO_TARGET_RIGHT, InfoTip } from '@/components/ui/InfoTip';
import { InlineReport } from '@/components/ui/ReportControl';
import { useToast } from '@/components/ui/Toast';
import { phone } from '@/lib/format';
import {
  MAIL_CHECKING, MAIL_CHECK_FAILED, MAIL_SAVE_FIRST, MAIL_SEND_FAILED, NOTE_CHANGED, NOTE_EMAIL, NOTE_LOAD_FAILED, NOTE_SAVE_FAILED,
  callNote, checkCallInvite, mailButton, mailLine, otherParty, saveCallNote, sendCallInvite, sentNotice, toE164,
  type CallNoteRow, type CallReason, type MailCheck, type NoteCall,
} from './data';

interface Draft { name: string; phone: string; email: string; booking_ref: string; reason: '' | CallReason; needs_callback: boolean; notes: string }
type TextKey = 'name' | 'phone' | 'email' | 'booking_ref' | 'notes';
const TEXT_KEYS: TextKey[] = ['name', 'phone', 'email', 'booking_ref', 'notes'];
const REASONS: { value: CallReason; label: string }[] = [
  { value: 'booking', label: 'Booking' }, { value: 'change', label: 'Change' }, { value: 'cancel', label: 'Cancel' },
  { value: 'question', label: 'Question' }, { value: 'other', label: 'Other' },
];

const shown = (n: string | null | undefined) => (n ? (toE164(n) ? phone(n) : n) : '');
function draftOf(call: NoteCall | null, note: CallNoteRow | null): Draft {
  const other = call ? otherParty(call) : null;
  return {
    name: note?.name ?? '', phone: note?.phone ? shown(note.phone) : shown(other), email: note?.email ?? '',
    booking_ref: note?.booking_ref ?? '', reason: note?.reason ?? '', needs_callback: note?.needs_callback ?? false, notes: note?.notes ?? '',
  };
}
// what the server would store for a typed value: trimmed, '' = nothing, a US phone in one form
const textOf = (k: TextKey, v: string) => { const t = v.trim(); return t === '' ? null : k === 'phone' ? (toE164(t) ?? t) : t; };
const same = (a: Draft, b: Draft) => (Object.keys(a) as (keyof Draft)[]).every((k) => a[k] === b[k]);

/** What the call panel needs from the notes it shows inline: whether something is unsaved, and Save. */
export interface NotesControl { dirty: boolean; save(): Promise<boolean> }

export function CallNotesDialog({ callId, open, onClose, live, inline = false, control }: {
  callId: string | null; open: boolean; onClose(): void; live?: { hangup(): void } | null;
  /** In the call panel: the same fields and Save, on the page instead of in a popup (no Close; the panel closes). */
  inline?: boolean; control?: MutableRefObject<NotesControl | null>;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['call-note', callId], queryFn: () => callNote(callId!), enabled: open && callId !== null });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [base, setBase] = useState<Draft | null>(null); // what the fields showed when they were filled
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'saved' | 'not-saved' | null>(null);
  const toast = useToast();
  // the server's answer for the typed email (null check = still asking), and the send in flight
  const [mail, setMail] = useState<{ check: MailCheck | null; error: string | null } | null>(null);
  const [mailNote, setMailNote] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [recheck, setRecheck] = useState(0);
  const asked = useRef(0);

  // a new call or a fresh open starts from what is stored
  useEffect(() => { setDraft(null); setBase(null); setError(null); setStatus(null); setMailNote(null); }, [callId, open]);
  useEffect(() => {
    if (!open || !q.data || draft !== null) return;
    const d = draftOf(q.data.call, q.data.note);
    setDraft(d); setBase(d);
  }, [open, q.data, draft]);

  const call = q.data?.call ?? null;
  const note = q.data?.note ?? null;
  const dirty = draft !== null && base !== null && !same(draft, base);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => { setDraft((d) => (d ? { ...d, [k]: v } : d)); setStatus(null); };
  const locked = busy || sending;

  // ask about an email the server would save, 400 ms after the typing stops; only the newest answer is shown
  const mailKey = draft && NOTE_EMAIL.test(draft.email.trim()) ? draft.email.trim().toLowerCase() : '';
  useEffect(() => {
    const mine = ++asked.current;
    setMailNote(null);
    if (!open || !callId || !mailKey) { setMail(null); return; }
    setMail({ check: null, error: null });
    const t = window.setTimeout(() => {
      void checkCallInvite(callId, mailKey).then((r) => {
        if (mine !== asked.current) return;
        setMail(r.ok && r.data?.action ? { check: r.data, error: null } : { check: null, error: r.ok ? MAIL_CHECK_FAILED : r.message || MAIL_CHECK_FAILED });
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [open, callId, mailKey, recheck]);

  /** true = nothing left unsaved. */
  const save = async (): Promise<boolean> => {
    if (!draft || !callId) return true;
    const body: Parameters<typeof saveCallNote>[0] = { call_id: callId };
    for (const k of TEXT_KEYS) { const v = textOf(k, draft[k]); if (v !== (note?.[k] ?? null)) body[k] = v; }
    const reason = draft.reason === '' ? null : draft.reason;
    if (reason !== (note?.reason ?? null)) body.reason = reason;
    if (draft.needs_callback !== (note?.needs_callback ?? false)) body.needs_callback = draft.needs_callback;
    if ('notes' in body && note) body.version = note.version;
    if (Object.keys(body).length === 1) { setBase(draft); setStatus('saved'); return true; }
    setBusy(true);
    setError(null);
    const r = await saveCallNote(body);
    setBusy(false);
    if (!r.ok) {
      setStatus('not-saved');
      if (r.message === NOTE_CHANGED) {
        // someone else saved first: show what is stored now, and say so
        const fresh = await qc.fetchQuery({ queryKey: ['call-note', callId], queryFn: () => callNote(callId), staleTime: 0 }).catch(() => null);
        if (fresh) { const d = draftOf(fresh.call, fresh.note); setDraft(d); setBase(d); }
        setError(NOTE_CHANGED);
      } else setError(r.message || NOTE_SAVE_FAILED);
      return false;
    }
    const saved = r.data?.note ?? null;
    const d = saved ? draftOf(call, saved) : draft;
    qc.setQueryData(['call-note', callId], { call, note: saved ?? note });
    setDraft(d); setBase(d); setStatus('saved');
    const eventId = saved?.event_id ?? call?.event_id;
    if (eventId) void qc.invalidateQueries({ queryKey: ['event-calls', eventId] });
    void qc.invalidateQueries({ queryKey: ['call-note', callId] });
    return true;
  };
  const close = async () => {
    if (locked) return;
    if (dirty && !(await save())) return;
    onClose();
  };
  const mailLabel = mailButton(mail?.check?.action);
  /** Saves the note, then has the server email the address now saved on it. A save that fails sends nothing. */
  const sendMail = async () => {
    if (!callId || !draft || !mailLabel || locked) return;
    const email = draft.email.trim();
    setSending(true);
    setMailNote(null);
    setError(null);
    try {
      if (!(await save())) { setMailNote(MAIL_SAVE_FIRST); return; }
      const r = await sendCallInvite(callId, email);
      if (!r.ok) { setError(r.message || MAIL_SEND_FAILED); return; }
      const notice = r.data ? sentNotice(r.data) : null;
      if (notice) toast.success(notice);
      else if (r.data?.status === 'failed' || !r.data?.action) setError(MAIL_SEND_FAILED);
      setRecheck((n) => n + 1); // the line shows the server's answer after the send
    } finally {
      setSending(false);
    }
  };

  // the panel asks before it closes
  useEffect(() => { if (control) control.current = { dirty, save }; });

  const loadFailed = q.isError || (q.isSuccess && !call);
  const footer = draft && (
    <>
      {status && <span className="mr-auto self-center text-sm text-ink-2" data-testid="call-notes-status">{status === 'saved' ? 'Saved' : 'Not saved'}</span>}
      {live && !inline && <Button variant="danger" className="max-sm:min-h-11" onClick={live.hangup} data-testid="call-notes-hangup">Hang up</Button>}
      {error !== null && dirty && !inline && <Button variant="secondary" className="max-sm:min-h-11" disabled={locked} onClick={onClose} data-testid="call-notes-discard">Close without saving</Button>}
      <Button variant="primary" className="max-sm:min-h-11" disabled={locked} onClick={() => void save()} data-testid="call-notes-save">{busy ? 'Saving…' : 'Save'}</Button>
    </>
  );
  const body = (
      <div data-testid="call-notes-dialog" className="flex flex-col gap-4">
        {loadFailed ? (
          <div className="flex flex-wrap items-center gap-2">
            <p role="alert" className="text-st-danger" data-testid="call-notes-error">{NOTE_LOAD_FAILED}</p>
            <InlineReport message={NOTE_LOAD_FAILED} />
          </div>
        ) : !draft ? <p className="text-ink-2">Loading…</p> : (
          <>
            <div className={inline ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2'}>
              <TextField label="Name" value={draft.name} maxLength={120} autoComplete="off" onChange={(e) => set('name', e.target.value)} data-testid="call-notes-name" />
              <TextField label="Phone" type="tel" value={draft.phone} maxLength={32} autoComplete="off" onChange={(e) => set('phone', e.target.value)} data-testid="call-notes-phone" />
              <div className="flex min-w-0 flex-col">
                <TextField label="Email" type="email" value={draft.email} maxLength={254} autoComplete="off" disabled={sending} onChange={(e) => set('email', e.target.value)} data-testid="call-notes-email" />
                {mailKey && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="call-notes-mail">
                    <p data-testid="call-notes-mail-status" aria-live="polite" className="min-w-0 flex-1 basis-40 text-sm text-ink-2 empty:hidden">
                      {mailNote ?? (mail?.check ? mailLine(mail.check) : mail?.error ? '' : MAIL_CHECKING)}
                    </p>
                    {mailLabel && (
                      <div className="flex w-full items-center gap-3 sm:w-auto">
                        <Button variant="secondary" className="min-w-0 flex-1 max-sm:min-h-11 sm:flex-none" busy={sending} busyLabel="Sending…" disabled={busy}
                          onClick={() => void sendMail()} data-testid="call-notes-mail-send">{mailLabel}</Button>
                        <InfoTip title="Email the caller" align="end" className={INFO_TARGET_RIGHT}>
                          Resend confirmation emails this guest’s booking details and link again. Send invite email sends the registration link and the event website to someone who hasn’t booked. Each address can get three of these an hour.
                        </InfoTip>
                      </div>
                    )}
                    {mail?.error && (
                      <div className="flex w-full flex-wrap items-center gap-2">
                        <p role="alert" className="text-sm text-st-danger" data-testid="call-notes-mail-error">{mail.error}</p>
                        <InlineReport message={mail.error} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <TextField label="Booking / confirmation #" value={draft.booking_ref} maxLength={64} autoComplete="off" onChange={(e) => set('booking_ref', e.target.value)} data-testid="call-notes-booking" />
            </div>
            <SelectField label="Reason for call" value={draft.reason} onChange={(e) => set('reason', e.target.value as Draft['reason'])} data-testid="call-notes-reason">
              <option value="">—</option>
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </SelectField>
            <div className="relative pr-8">
              <Checkbox label="Needs a call back" checked={draft.needs_callback} onChange={(e) => set('needs_callback', e.target.checked)} data-testid="call-notes-callback" />
              <InfoTip title="Call back" className="absolute right-0 top-0" align="end">
                Marks this call so the team sees it in Recent calls. Calling the number back from the call, or unticking this, clears it.
              </InfoTip>
            </div>
            <TextArea label="Notes" rows={5} value={draft.notes} maxLength={4000} onChange={(e) => set('notes', e.target.value)} data-testid="call-notes-notes" />
            {error && (
              <div className="flex flex-wrap items-center gap-2">
                <p role="alert" className="text-st-danger" data-testid="call-notes-error">{error}</p>
                <InlineReport message={error} />
              </div>
            )}
          </>
        )}
      </div>
  );
  if (inline) {
    return (
      <section aria-label="Notes for this call" className="flex flex-col gap-4" data-testid="call-notes-inline">
        {body}
        {footer && <div className="flex flex-wrap items-center justify-end gap-2">{footer}</div>}
      </section>
    );
  }
  return (
    <Dialog
      open={open}
      onClose={() => void close()}
      title="Notes for this call"
      description={call ? `${call.direction === 'in' ? 'Call from' : 'Call to'} ${shown(otherParty(call)) || 'a hidden number'}` : undefined}
      size="md"
      closeDisabled={locked}
      footer={footer}
    >
      {body}
    </Dialog>
  );
}
